import React, { useState, useEffect } from 'react';
import {
  Select, Input, Button, Form, Spin, message, Descriptions, Card, Divider, Typography, Modal, Row, Col, Space, Steps
} from 'antd';
import {
  WalletOutlined, MobileOutlined, BankOutlined, SafetyOutlined,
  CreditCardOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { getNewRequest, postPayURequest } from '../../Utils/AppApi';
import {
  BBPS_IMWALLET_OPERATORS,
  BBPS_IMWALLET_FETCH_BILL,
  BBPS_IMWALLET_PAY_BILL,
  BBPS_IMWALLET_EXTRA_PARAMS
} from '../../Utils/appConstants';
import { useHistory } from "react-router-dom";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Invoice from './invoice';

const { Option } = Select;
const { Title, Text } = Typography;
const { Step } = Steps;

const PRIMARY_COLOR = '#C9262C';
const ACCENT_COLOR = '#FFDD09';

const safeNumber = (value, defaultValue = 0) => {
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

// ---- Mobile number validation ----
const isRepeatedDigits = (mobile) => /^(\d)\1{9}$/.test(mobile);
const isSequentialDigits = (mobile) => {
  const digits = mobile.split('').map(Number);
  const ascending = digits.every((d, i) => i === 0 || d === digits[i - 1] + 1);
  const descending = digits.every((d, i) => i === 0 || d === digits[i - 1] - 1);
  return ascending || descending;
};
const isValidMobile = (mobile) => {
  if (!/^[6-9]\d{9}$/.test(mobile)) {
    return { valid: false, message: 'Invalid mobile number. Must be a 10-digit Indian number starting with 6, 7, 8, or 9.' };
  }
  if (isRepeatedDigits(mobile)) {
    return { valid: false, message: 'Invalid mobile number. Repeated digits (e.g., 9999999999) are not allowed.' };
  }
  if (isSequentialDigits(mobile)) {
    return { valid: false, message: 'Invalid mobile number. Sequential digits (e.g., 1234567890) are not allowed.' };
  }
  return { valid: true, message: '' };
};

// ---- UPI handles ----
const UPI_HANDLES = [
  '@upi', '@paytm', '@ybl', '@ibl', '@axl',
  '@okaxis', '@okicici', '@oksbi', '@payu', '@fbl', '@sbi'
];

const generateUpiId = (mobile) => {
  if (!mobile) return '';
  const randomHandle = UPI_HANDLES[Math.floor(Math.random() * UPI_HANDLES.length)];
  return mobile + randomHandle;
};

// ---- Helper: Check if field is a mobile number field ----
const isMobileNumberField = (name, regex) => {
  if (!name && !regex) return false;
  const nameLower = (name || '').toLowerCase();
  if (nameLower.includes('mobile') || nameLower.includes('phone')) return true;
  if (regex) {
    const cleanRegex = regex.replace(/[\\^$]/g, '');
    if (cleanRegex.includes('[6-9]') && cleanRegex.includes('[0-9]{9}')) return true;
  }
  return false;
};

const CommonBillPayment = ({ category }) => {
  const history = useHistory();

  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [account, setAccount] = useState('');
  const [mobile, setMobile] = useState('');
  const [upiId, setUpiId] = useState('');
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const [billData, setBillData] = useState(null);
  const [billFetchId, setBillFetchId] = useState(null);
  const [bbpsTxId, setBbpsTxId] = useState(null);
  const [fetchTransactionId, setFetchTransactionId] = useState(null);
  const [isPrepaid, setIsPrepaid] = useState(false);

  const [extraFields, setExtraFields] = useState([]);
  const [extraValues, setExtraValues] = useState({});

  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [partialAmount, setPartialAmount] = useState(null);
  const [customAmountInput, setCustomAmountInput] = useState('');

  // ---- Build extra fields (skips ad1 for Fastag and hides mobile fields) ----
  const buildExtraFieldsFromOperator = (operator) => {
    const fields = [];
    const adConfigs = [
      { key: 'ad1', name: operator.ad1, optional: operator.ad1_optional, regex: operator.ad1_regex },
      { key: 'ad2', name: operator.ad2, optional: operator.ad2_optional, regex: operator.ad2_regex },
      { key: 'ad3', name: operator.ad3, optional: operator.ad3_optional, regex: operator.ad3_regex },
      { key: 'ad4', name: operator.ad4, optional: operator.ad4_optional, regex: operator.ad4_regex },
      { key: 'ad5', name: operator.ad5, optional: operator.ad5_optional, regex: operator.ad5_regex },
    ];

    const newExtraValues = {};

    if (operator.spkey === '294') {
      // Skip ad1
    } else {
      adConfigs.forEach(cfg => {
        if (cfg.name && cfg.name !== 'NA' && cfg.name !== '') {
          const isMobile = isMobileNumberField(cfg.name, cfg.regex);
          if (isMobile) {
            newExtraValues[cfg.key] = mobile || '';
          } else {
            fields.push({
              key: cfg.key,
              label: cfg.name,
              required: cfg.optional === 'FALSE',
              regex: cfg.regex,
            });
            newExtraValues[cfg.key] = '';
          }
        } else {
          newExtraValues[cfg.key] = '';
        }
      });
    }

    if (operator.spkey === '294') {
      newExtraValues.ad1 = operator.ad1_regex || '';
    }

    setExtraValues(prev => ({ ...prev, ...newExtraValues }));
    return fields;
  };

  // When mobile changes, update any hidden mobile fields
  useEffect(() => {
    if (!selectedOperator) return;
    const adConfigs = [
      { key: 'ad1', name: selectedOperator.ad1, regex: selectedOperator.ad1_regex },
      { key: 'ad2', name: selectedOperator.ad2, regex: selectedOperator.ad2_regex },
      { key: 'ad3', name: selectedOperator.ad3, regex: selectedOperator.ad3_regex },
      { key: 'ad4', name: selectedOperator.ad4, regex: selectedOperator.ad4_regex },
      { key: 'ad5', name: selectedOperator.ad5, regex: selectedOperator.ad5_regex },
    ];
    const updates = {};
    adConfigs.forEach(cfg => {
      if (cfg.name && cfg.name !== 'NA' && cfg.name !== '') {
        if (isMobileNumberField(cfg.name, cfg.regex)) {
          updates[cfg.key] = mobile || '';
        }
      }
    });
    if (Object.keys(updates).length > 0) {
      setExtraValues(prev => ({ ...prev, ...updates }));
    }
  }, [mobile, selectedOperator]);

  // Generate UPI ID when mobile changes
  useEffect(() => {
    if (mobile && mobile.length === 10) {
      const validation = isValidMobile(mobile);
      if (validation.valid) {
        setUpiId(generateUpiId(mobile));
      } else {
        setUpiId('');
      }
    } else {
      setUpiId('');
    }
  }, [mobile]);

  // Fetch operators
  useEffect(() => {
    if (!category) return;
    const fetchOperators = async () => {
      setLoading(true);
      try {
        const response = await getNewRequest(BBPS_IMWALLET_OPERATORS, { category }, history);
        if (response.success && Array.isArray(response.data)) {
          const mapped = response.data.map((item, index) => ({
            ...item,
            id: item.BillerName ? item.BillerName : `${item.spkey}_${index}`,
            operator: item.BillerName || item.operator || 'Unnamed',
            state: item.status || item.state || 'Active',
            name: item.account_name || item.name || 'Account Number',
            ad1: item.ad1_name || item.ad1 || '',
            ad2: item.ad2_name || item.ad2 || '',
            ad3: item.ad3_name || item.ad3 || '',
            ad4: item.ad4_name || item.ad4 || '',
            ad5: item.ad5_name || item.ad5 || '',
            amount_exactness: item.amount_exactness,
            ad1_optional: item.ad1_optional,
            ad2_optional: item.ad2_optional,
            ad3_optional: item.ad3_optional,
            ad4_optional: item.ad4_optional,
            ad5_optional: item.ad5_optional,
            ad1_regex: item.ad1_regex,
            ad2_regex: item.ad2_regex,
            ad3_regex: item.ad3_regex,
            ad4_regex: item.ad4_regex,
            ad5_regex: item.ad5_regex,
            cash_max: item.cash_max,
            upi_min: item.upi_min,
            upi_max: item.upi_max,
            fetchRequired: item.fetchRequired,
            bbpsEnabled: item.bbpsEnabled,
          }));
          setOperators(mapped);
        } else {
          toast.error(response.message || 'Failed to load billers');
          setOperators([]);
        }
      } catch (err) {
        console.error('Fetch operators error:', err);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOperators();
  }, [category, history]);

  // Handle operator selection
  const handleOperatorChange = async (operatorId) => {
    const operator = operators.find(op => op.id === operatorId);
    if (!operator) {
      toast.error('Operator not found');
      return;
    }

    setSelectedOperator(operator);
    setBillData(null);
    setBillFetchId(null);
    setBbpsTxId(null);
    setFetchTransactionId(null);
    setExtraValues({});
    setExtraFields([]);
    setCurrentStep(0);
    setReceiptData(null);
    setPartialAmount(null);
    setCustomAmountInput('');
    setIsPrepaid(false);

    const fields = buildExtraFieldsFromOperator(operator);
    setExtraFields(fields);

    if (['267', '197', '282'].includes(operator.spkey)) {
      try {
        const extraResp = await getNewRequest(BBPS_IMWALLET_EXTRA_PARAMS, { spkey: operator.spkey }, history);
        if (extraResp.success && extraResp.data && Array.isArray(extraResp.data)) {
          const dynamicFields = extraResp.data.map((param, idx) => ({
            key: `custom_${idx}`,
            label: param.label || param.paramName || `Additional Field ${idx + 1}`,
            required: false,
          }));
          setExtraFields(prev => [...prev, ...dynamicFields]);
          const dynamicInitial = {};
          dynamicFields.forEach(field => { dynamicInitial[field.key] = ''; });
          setExtraValues(prev => ({ ...prev, ...dynamicInitial }));
        }
      } catch (err) {
        toast.warning('Could not load additional fields for this biller');
      }
    }
  };

  const handleExtraFieldChange = (key, value) => {
    setExtraValues(prev => ({ ...prev, [key]: value }));
  };

  const validateMobile = () => {
    const validation = isValidMobile(mobile);
    if (!validation.valid) {
      toast.error(validation.message);
      return false;
    }
    return true;
  };

  // ---- Helper: Check if operator is prepaid ----
  const isPrepaidOperator = (operator) => {
    if (!operator) return false;
    const fetchReq = operator.fetchRequired ? String(operator.fetchRequired).trim().toLowerCase() : '';
    const bbps = operator.bbpsEnabled ? String(operator.bbpsEnabled).trim().toUpperCase() : '';
    return fetchReq === 'not required' || bbps === 'FALSE';
  };

  // ---- Handle Fetch Bill (for non-prepaid) ----
  const handleFetchBill = async () => {
    if (!selectedOperator) return toast.error('Please select an operator');
    if (!account) return toast.error(`Please enter ${selectedOperator.name || 'account number'}`);
    if (!mobile) return toast.error('Please enter registered mobile number');
    if (!validateMobile()) return;

    setLoading(true);
    try {
      const payload = {
        account,
        spkey: selectedOperator.spkey,
        cust_mbl: mobile,
        ad1: extraValues.ad1 || '',
        ad2: extraValues.ad2 || '',
        ad3: extraValues.ad3 || '',
        ad4: extraValues.ad4 || '',
        ad5: extraValues.ad5 || '',
        category: category,
        operatorName: selectedOperator.operator
      };
      const response = await postPayURequest(BBPS_IMWALLET_FETCH_BILL, payload, history);

      if (response.success && response.data) {
        let billDetails = response.data;
        const commissionData = response.commissionData || {};

        if (billDetails.billFetchId === 'NA' || response.isPrepaid) {
          setIsPrepaid(true);
          setBillData({
            ...billDetails,
            payamount: '0',
            billedamount: '0',
            customerName: 'Prepaid Customer'
          });
          setBillFetchId('NA');
          setBbpsTxId(response.bbpsTxId);
          setFetchTransactionId(response.fetchTransactionId);
          setCurrentStep(1);
          toast.info('Prepaid biller – enter amount to pay');
          return;
        }

        billDetails.serviceAmount = commissionData.serviceAmount || 0;
        billDetails.commissionAmount = commissionData.commissionAmount || 0;
        billDetails.tdsAmount = commissionData.tdsAmount || 0;
        setBillData(billDetails);
        setBillFetchId(billDetails.billFetchId);
        setBbpsTxId(response.bbpsTxId);
        setFetchTransactionId(response.fetchTransactionId);
        setPartialAmount(null);
        setCustomAmountInput('');
        setCurrentStep(1);
        toast.success('Bill fetched successfully');
      } else {
        toast.error(response.message || 'Failed to fetch bill');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---- Generate receipt data ----
  const generateReceipt = (status, orderId, amountPaid, errorMsg = null) => {
    return {
      txnStatus: status,
      customerPhoneNumber: mobile,
      category: category,
      txtId: orderId,
      apiResponse: {
        additionalParams: {
          txnReferenceId: orderId,
          billerReferenceNumber: orderId,
          errorMessage: errorMsg,
        }
      },
      apiRequest: { timeStamp: new Date().toISOString() },
      billerName: selectedOperator.operator,
      accountNumber: account,
      paidAmount: amountPaid,
      transactionId: orderId,
    };
  };

  // ---- Handle Prepaid Payment (call fetch-bill in background, then pay) ----
  const handlePrepaidPayment = async () => {
    if (!selectedOperator) return toast.error('Please select an operator');
    if (!account) return toast.error(`Please enter ${selectedOperator.name || 'account number'}`);
    if (!mobile) return toast.error('Please enter registered mobile number');
    if (!validateMobile()) return;

    if (!customAmountInput || parseFloat(customAmountInput) <= 0) {
      toast.error('Please enter a valid amount to pay');
      return;
    }
    const amountToPay = parseFloat(customAmountInput);

    const upiIdToSend = generateUpiId(mobile);
    if (!upiIdToSend) {
      toast.error('Could not generate UPI ID. Please check mobile number.');
      return;
    }

    setPaying(true);
    try {
      // ---- Step 1: Call fetch-bill API in background ----
      const fetchPayload = {
        account,
        spkey: selectedOperator.spkey,
        cust_mbl: mobile,
        ad1: extraValues.ad1 || '',
        ad2: extraValues.ad2 || '',
        ad3: extraValues.ad3 || '',
        ad4: extraValues.ad4 || '',
        ad5: extraValues.ad5 || '',
        category: category,
        operatorName: selectedOperator.operator
      };
      const fetchResponse = await postPayURequest(BBPS_IMWALLET_FETCH_BILL, fetchPayload, history);
      if (!fetchResponse.success) {
        toast.error(fetchResponse.message || 'Failed to initiate prepaid payment');
        setPaying(false);
        return;
      }
      const { bbpsTxId, fetchTransactionId } = fetchResponse;
      if (!bbpsTxId) {
        toast.error('Invalid response from fetch-bill');
        setPaying(false);
        return;
      }

      // ---- Step 2: Call pay-bill API ----
      const payPayload = {
        bbpsTxId: bbpsTxId,
        amount: amountToPay,
        orderid: fetchTransactionId,
        billFetchId: 'NA',  // as returned from fetch
        cust_mbl: mobile,
        ad1: extraValues.ad1 || '',
        ad2: extraValues.ad2 || '',
        ad3: extraValues.ad3 || '',
        ad4: extraValues.ad4 || '',
        ad5: extraValues.ad5 || '',
        upiId: upiIdToSend,
        spkey: selectedOperator.spkey,
        account: account,
        category: category,
      };
      const payResponse = await postPayURequest(BBPS_IMWALLET_PAY_BILL, payPayload, history);

      let status = 'PAYMENT_FAILED';
      let orderId = fetchTransactionId;
      let errorMsg = null;

      if (payResponse.success) {
        const result = payResponse.data;
        orderId = result.orderid || fetchTransactionId;
        if (result.status === 'SUCCESS') {
          status = 'PAYMENT_SUCCESS';
          toast.success('Payment successful!');
        } else if (result.status === 'PENDING') {
          status = 'PAYMENT_PENDING';
          toast.warning('Transaction is pending. Please check status later.');
        } else {
          errorMsg = result.msg || 'Payment failed. Please try again.';
          toast.error(errorMsg);
        }
      } else {
        errorMsg = payResponse.message || 'Payment request failed. Please try again.';
        toast.error(errorMsg);
      }

      const receipt = generateReceipt(status, orderId, amountToPay, errorMsg);
      setReceiptData(receipt);
      setCurrentStep(2);

    } catch (err) {
      toast.error(err.message || 'Payment failed. Please try again.');
      const receipt = generateReceipt('PAYMENT_FAILED', 'NA', amountToPay, err.message);
      setReceiptData(receipt);
      setCurrentStep(2);
    } finally {
      setPaying(false);
    }
  };

  const handlePartialAmountChange = (e) => {
    let value = e.target.value;
    if (value === '') {
      setCustomAmountInput('');
      setPartialAmount(null);
      return;
    }
    let num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setCustomAmountInput(num.toString());
    setPartialAmount(num);
  };

  // Execute payment (for Step 2)
  const executeBillPayment = async () => {
    if (!billData) {
      toast.error('No bill data to pay');
      setIsConfirmModalOpen(false);
      return;
    }
    if (!bbpsTxId) {
      toast.error('Transaction ID missing. Please fetch bill again.');
      setIsConfirmModalOpen(false);
      return;
    }
    if (!validateMobile()) {
      setIsConfirmModalOpen(false);
      return;
    }

    const upiIdToSend = generateUpiId(mobile);
    if (!upiIdToSend) {
      toast.error('Could not generate UPI ID. Please check mobile number.');
      setIsConfirmModalOpen(false);
      return;
    }

    let amountToPay;
    if (isPrepaid) {
      if (!customAmountInput || parseFloat(customAmountInput) <= 0) {
        toast.error('Please enter a valid amount to pay');
        return;
      }
      amountToPay = parseFloat(customAmountInput);
    } else {
      const defaultAmount = safeNumber(billData.payamount, safeNumber(billData.billedamount));
      amountToPay = defaultAmount;
      if (billData.partPayment && partialAmount !== null && partialAmount > 0) {
        if (selectedOperator?.amount_exactness !== 'Exact') {
          amountToPay = partialAmount;
        } else {
          toast.warning('This bill requires exact payment, partial amount ignored.');
        }
      }
    }

    setPaying(true);
    try {
      const orderId = fetchTransactionId;
      const payload = {
        bbpsTxId: bbpsTxId,
        amount: amountToPay,
        orderid: orderId,
        billFetchId: billFetchId || 'NA',
        cust_mbl: mobile,
        ad1: extraValues.ad1 || '',
        ad2: extraValues.ad2 || '',
        ad3: extraValues.ad3 || '',
        ad4: extraValues.ad4 || '',
        ad5: extraValues.ad5 || '',
        upiId: upiIdToSend,
      };

      if (isPrepaid) {
        payload.spkey = selectedOperator.spkey;
        payload.account = account;
        payload.category = category;
      }

      const response = await postPayURequest(BBPS_IMWALLET_PAY_BILL, payload, history);

      let status = 'PAYMENT_FAILED';
      let errorMsg = null;
      if (response.success) {
        const result = response.data;
        if (result.status === 'SUCCESS') {
          status = 'PAYMENT_SUCCESS';
          toast.success('Payment successful!');
        } else if (result.status === 'PENDING') {
          status = 'PAYMENT_PENDING';
          toast.warning('Transaction is pending. Please check status later.');
        } else {
          errorMsg = result.msg || 'Payment failed. Please try again.';
          toast.error(errorMsg);
        }
      } else {
        errorMsg = response.message || 'Payment request failed. Please try again.';
        toast.error(errorMsg);
      }

      const receipt = generateReceipt(status, orderId, amountToPay, errorMsg);
      setReceiptData(receipt);
      setCurrentStep(2);

    } catch (err) {
      toast.error(err.message || 'Payment failed. Please try again.');
      const receipt = generateReceipt('PAYMENT_FAILED', fetchTransactionId, amountToPay, err.message);
      setReceiptData(receipt);
      setCurrentStep(2);
    } finally {
      setPaying(false);
      setIsConfirmModalOpen(false);
    }
  };

  const confirmPayment = () => {
    if (!billData) return toast.error('Please fetch bill first');
    setIsConfirmModalOpen(true);
  };

  const handleConfirmOk = () => {
    executeBillPayment();
  };

  const handleConfirmCancel = () => {
    setIsConfirmModalOpen(false);
  };

  const handleBackToStep1 = () => {
    setCurrentStep(0);
    setBillData(null);
    setBillFetchId(null);
    setBbpsTxId(null);
    setFetchTransactionId(null);
    setReceiptData(null);
    setPartialAmount(null);
    setCustomAmountInput('');
    setIsPrepaid(false);
    setIsConfirmModalOpen(false);
  };

  const handleNewPayment = () => {
    setSelectedOperator(null);
    setAccount('');
    setMobile('');
    setExtraFields([]);
    setExtraValues({});
    setBillData(null);
    setBillFetchId(null);
    setBbpsTxId(null);
    setFetchTransactionId(null);
    setReceiptData(null);
    setPartialAmount(null);
    setCustomAmountInput('');
    setCurrentStep(0);
    setIsPrepaid(false);
    setIsConfirmModalOpen(false);
  };

  // ---- Render Step 1 ----
  const renderStep1 = () => {
    const isPrepaidOp = isPrepaidOperator(selectedOperator);

    return (
      <Form layout="vertical" size="small" style={{ marginTop: 0 }}>
        <Row gutter={[12, 8]}>
          <Col xs={24}>
            <Form.Item label="Select Biller" required style={{ marginBottom: 8 }}>
              <Select
                placeholder="Choose your biller"
                onChange={handleOperatorChange}
                value={selectedOperator?.id}
                loading={loading}
                showSearch
                optionFilterProp="children"
                size="middle"
                style={{ borderRadius: 8 }}
              >
                {operators.map(op => (
                  <Option key={op.id} value={op.id}>
                    {op.operator}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {selectedOperator && (
            <>
              <Col xs={24} md={12}>
                <Form.Item label={selectedOperator.name || 'Account Number'} required style={{ marginBottom: 8 }}>
                  <Input
                    prefix={<BankOutlined />}
                    placeholder={`Enter ${(selectedOperator.name || 'account number').toLowerCase()}`}
                    value={account}
                    onChange={e => setAccount(e.target.value)}
                    size="middle"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Registered Mobile Number" required style={{ marginBottom: 8 }}>
                  <Input
                    prefix={<MobileOutlined />}
                    placeholder="10-digit mobile number"
                    value={mobile}
                    onChange={e => setMobile(e.target.value)}
                    maxLength={10}
                    size="middle"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>
              </Col>

              {extraFields.length > 0 && (
                <Col xs={24}>
                  <Divider orientation="left" style={{ margin: '8px 0', fontSize: 12 }}>Additional Information</Divider>
                  <Row gutter={[12, 8]}>
                    {extraFields.map(field => (
                      <Col xs={24} md={12} key={field.key}>
                        <Form.Item
                          label={field.label}
                          required={field.required}
                          rules={field.regex ? [{ pattern: new RegExp(field.regex), message: `Invalid ${field.label} format` }] : []}
                          style={{ marginBottom: 8 }}
                        >
                          <Input
                            value={extraValues[field.key] || ''}
                            onChange={e => handleExtraFieldChange(field.key, e.target.value)}
                            placeholder={`Enter ${field.label}`}
                            size="middle"
                            style={{ borderRadius: 8 }}
                          />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                </Col>
              )}

              {isPrepaidOp && (
                <Col xs={24}>
                  <Form.Item label="Enter Amount to Pay (₹)" required style={{ marginBottom: 8 }}>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={customAmountInput}
                      onChange={handlePartialAmountChange}
                      prefix="₹"
                      size="middle"
                      style={{ borderRadius: 8 }}
                      placeholder="Enter amount"
                    />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24}>
                {isPrepaidOp ? (
                  <Button
                    type="primary"
                    onClick={handlePrepaidPayment}
                    loading={paying}
                    size="middle"
                    block
                    icon={<WalletOutlined />}
                    style={{
                      borderRadius: 40,
                      height: 50,
                      fontWeight: 500,
                      background: PRIMARY_COLOR,
                      border: 'none'
                    }}
                  >
                    Pay Now
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={handleFetchBill}
                    loading={loading}
                    size="middle"
                    block
                    icon={<WalletOutlined />}
                    style={{
                      borderRadius: 40,
                      height: 50,
                      fontWeight: 500,
                      background: PRIMARY_COLOR,
                      border: 'none'
                    }}
                  >
                    Fetch Bill
                  </Button>
                )}
              </Col>
            </>
          )}
        </Row>
      </Form>
    );
  };

  // ---- Render Step 2 (Bill Summary) ----
  const renderStep2 = () => {
    if (!billData) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" tip="Loading bill details..." />
        </div>
      );
    }

    const totalAmount = safeNumber(billData.billedamount);
    const defaultPayable = safeNumber(billData.payamount, totalAmount);
    const finalAmount = partialAmount !== null ? safeNumber(partialAmount) : defaultPayable;
    const isPrepaidBiller = isPrepaid || billData.billFetchId === 'NA';

    const isExact = selectedOperator?.amount_exactness === 'Exact';

    let paymentMode = 'UPI';
    if (selectedOperator) {
      const amountPaise = Math.round(totalAmount * 100);
      const cashMax = parseInt(selectedOperator.cash_max) || 0;
      if (cashMax > 0 && amountPaise <= cashMax) {
        paymentMode = 'Cash';
      }
    }

    return (
      <>
        <Card
          title={isPrepaidBiller ? "Prepaid Payment" : "Bill Summary"}
          size="small"
          style={{ marginBottom: 12, borderRadius: 12 }}
          headStyle={{ borderBottom: '1px solid #f0f0f0', fontWeight: 500, fontSize: 14, padding: '8px 12px' }}
          bodyStyle={{ padding: '12px' }}
        >
          {isPrepaidBiller ? (
            <div>
              <Form.Item label="Enter Amount to Pay (₹)" required style={{ marginBottom: 8 }}>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={customAmountInput}
                  onChange={handlePartialAmountChange}
                  prefix="₹"
                  size="middle"
                  style={{ borderRadius: 8 }}
                  placeholder="Enter amount"
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 11 }}>Enter the amount you want to recharge/pay.</Text>
            </div>
          ) : (
            <>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Customer Name" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
                  {billData.customerName || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Billed Amount" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, fontWeight: 500 }}>
                  ₹{totalAmount.toFixed(2)}
                </Descriptions.Item>
                {billData.partPayment && !isExact && (
                  <Descriptions.Item label="Partial Payment" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
                    <span style={{ color: '#52c41a' }}>Allowed</span> – You can pay any positive amount
                  </Descriptions.Item>
                )}
                {isExact && (
                  <Descriptions.Item label="Payment Type" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
                    <span style={{ color: '#faad14' }}>Exact Amount Only</span> – You must pay ₹{totalAmount.toFixed(2)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Due Date" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
                  {billData.dueDate || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Bill Date" labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12 }}>
                  {billData.billdate || 'N/A'}
                </Descriptions.Item>
              </Descriptions>

              {/* ----- Display `Info` object (Fastag details) ----- */}
              {billData.Info && typeof billData.Info === 'object' && Object.keys(billData.Info).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Divider orientation="left" style={{ margin: '4px 0 8px 0', fontSize: 13 }}>
                    <Text strong style={{ color: PRIMARY_COLOR }}>Additional Details</Text>
                  </Divider>
                  <Card
                    size="small"
                    bordered={true}
                    style={{
                      background: '#fafafa',
                      borderRadius: 8,
                      border: '1px solid #e8e8e8'
                    }}
                    bodyStyle={{ padding: '8px 12px' }}
                  >
                    <Row gutter={[8, 6]}>
                      {Object.entries(billData.Info).map(([key, value]) => (
                        <Col xs={24} sm={12} key={key}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Text strong style={{ fontSize: 13, width: '55%', color: '#595959' }}>
                              {key}:
                            </Text>
                            <Text style={{ fontSize: 13, color: '#262626', wordBreak: 'break-word' }}>
                              {value}
                            </Text>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                </div>
              )}
            </>
          )}

          {billData.partPayment && !isPrepaidBiller && !isExact && (
            <div style={{ marginTop: 12 }}>
              <Form.Item label="Enter Payment Amount (₹)" style={{ marginBottom: 8 }}>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customAmountInput}
                  onChange={handlePartialAmountChange}
                  prefix="₹"
                  size="middle"
                  style={{ borderRadius: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>Enter any positive amount (no maximum limit)</Text>
              </Form.Item>
            </div>
          )}

          {isExact && !isPrepaidBiller && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
              <Text style={{ fontSize: 12, color: '#d48806' }}>
                <strong>Exact payment required:</strong> You must pay exactly ₹{totalAmount.toFixed(2)}
              </Text>
            </div>
          )}
        </Card>

        <Row gutter={12}>
          <Col span={12}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBackToStep1}
              size="middle"
              block
              style={{
                borderRadius: 40,
                height: 50,
                borderColor: PRIMARY_COLOR,
                color: PRIMARY_COLOR,
                background: 'white'
              }}
            >
              Back
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              onClick={confirmPayment}
              loading={paying}
              size="middle"
              block
              icon={<WalletOutlined />}
              style={{
                background: PRIMARY_COLOR,
                borderColor: PRIMARY_COLOR,
                borderRadius: 40,
                height: 50
              }}
            >
              Pay Now {!isPrepaidBiller && `₹${finalAmount.toFixed(2)}`}
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  // ---- Render Step 3 (Invoice) ----
  const renderStep3 = () => (
    <div>
      <Invoice transactionId={receiptData?.transactionId} />
      {receiptData?.txnStatus === 'PAYMENT_FAILED' && (
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Button
            type="primary"
            onClick={() => {
              setCurrentStep(0);
              setReceiptData(null);
              setIsPrepaid(false);
              setBillData(null);
              setBillFetchId(null);
              setBbpsTxId(null);
              setFetchTransactionId(null);
              setCustomAmountInput('');
            }}
            style={{ borderRadius: 40, height: 50, width: '100%', maxWidth: 300 }}
          >
            Retry Payment
          </Button>
        </div>
      )}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <Button
          type="primary"
          icon={<ArrowLeftOutlined />}
          onClick={handleNewPayment}
          size="middle"
          style={{
            borderRadius: 40,
            height: 50,
            width: '100%',
            maxWidth: 300,
            background: PRIMARY_COLOR,
            borderColor: PRIMARY_COLOR
          }}
        >
          Pay Another Bill
        </Button>
      </div>
    </div>
  );

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
      <img src="../logo_small.png" alt="Website Logo" style={{ height: 50 }} />
      <div style={{ textAlign: 'center' }}>
        <Title level={4} style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Pay {category} Bill</Title>
      </div>
      <img src="../images/bharat_connect.png" alt="BBPS Logo" style={{ height: 50 }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 0 }}>
      <style>{`
        .ant-steps-item-icon {
          background: transparent !important;
        }
        .ant-steps-item-icon .ant-steps-icon {
          background: transparent !important;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon {
          border-color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon {
          color: ${PRIMARY_COLOR} !important;
        }
        .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after {
          background-color: ${PRIMARY_COLOR} !important;
        }
      `}</style>
      <Card
        bordered={false}
        style={{
          borderRadius: 20,
          boxShadow: '0 8px 20px -6px rgba(0,0,0,0.1)',
          background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
          padding: '12px 16px'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {header}
          <Divider style={{ margin: '4px 0' }} />
          <Steps current={currentStep} size="small" style={{ marginBottom: 12 }}>
            <Step title="Enter" icon={<BankOutlined />} />
            <Step title="Confirm" icon={<CreditCardOutlined />} />
            <Step title="Receipt" icon={<WalletOutlined />} />
          </Steps>

          {currentStep === 0 && renderStep1()}
          {currentStep === 1 && renderStep2()}
          {currentStep === 2 && renderStep3()}
        </Space>
      </Card>

      <Modal
        title={
          <Space size="small">
            <SafetyOutlined /> Confirm Payment
          </Space>
        }
        open={isConfirmModalOpen}
        onOk={handleConfirmOk}
        onCancel={handleConfirmCancel}
        okText="Yes, Pay Now"
        cancelText="Cancel"
        maskClosable={false}
        confirmLoading={paying}
      >
        <p>
          Are you sure you want to pay ₹
          {(() => {
            if (isPrepaid) {
              return (parseFloat(customAmountInput) || 0).toFixed(2);
            }
            const amount = partialAmount !== null
              ? safeNumber(partialAmount)
              : safeNumber(billData?.payamount, safeNumber(billData?.billedamount));
            return amount.toFixed(2);
          })()}?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default CommonBillPayment;